import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { LayoutDashboard, Users, LandPlot, Package, Flag, ShieldCheck, BarChart3, ScrollText, ArrowLeft, Menu, X, Crown } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';

const NAV_ITEMS = [
  { to: '/admin', icon: LayoutDashboard, label: 'Dashboard', end: true },
  { to: '/admin/listings', icon: LandPlot, label: 'Terreni' },
  { to: '/admin/batches', icon: Package, label: 'Partite' },
  { to: '/admin/users', icon: Users, label: 'Utenti' },
  { to: '/admin/verifications', icon: ShieldCheck, label: 'Verifiche', badge: 'verifications' },
  { to: '/admin/reports', icon: Flag, label: 'Segnalazioni', badge: 'reports' },
  { to: '/admin/analytics', icon: BarChart3, label: 'Analytics' },
  { to: '/admin/audit-log', icon: ScrollText, label: 'Audit Log' },
];

export default function AdminLayout() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [badges, setBadges] = useState({});
  const isSuper = user?.role === 'super_admin';

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get('/admin/dashboard');
        if (!alive) return;
        setBadges({
          verifications: data.users_pending_verification || 0,
          reports: data.reports_open || 0,
        });
      } catch {}
    };
    load();
    const id = setInterval(load, 60000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const SidebarInner = (
    <div className="flex flex-col h-full">
      <div className="px-5 py-6 border-b border-border/60">
        <Link to="/" className="text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground flex items-center gap-1"><ArrowLeft size={12}/> Torna al sito</Link>
        <div className="mt-4 flex items-center gap-2">
          {isSuper && <Crown size={16} className="text-terracotta"/>}
          <div className="font-display text-xl">Admin Console</div>
        </div>
        <div className="mt-1 text-xs text-muted-foreground">{user?.email}</div>
        <div className="mt-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-secondary">
          {isSuper ? 'SUPER ADMIN' : 'ADMIN'}
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto p-3 space-y-1">
        {NAV_ITEMS.map(it => (
          <NavLink key={it.to} to={it.to} end={it.end} onClick={()=>setOpen(false)}
            data-testid={`admin-nav-${it.label.toLowerCase().replace(/\s/g,'-')}`}
            className={({isActive})=>`flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition ${isActive?'bg-foreground text-background':'text-muted-foreground hover:text-foreground hover:bg-secondary'}`}>
            <it.icon size={16} strokeWidth={1.6}/>
            <span className="flex-1">{it.label}</span>
            {it.badge && badges[it.badge] > 0 && (
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-accent text-accent-foreground">{badges[it.badge]}</span>
            )}
          </NavLink>
        ))}
      </nav>
      <div className="p-4 text-[10px] text-muted-foreground border-t border-border/60">
        AgriNexus Admin · Backend RBAC LIVE
      </div>
    </div>
  );

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-secondary/30">
      <div className="max-w-[1600px] mx-auto lg:flex lg:gap-6">
        <aside className="hidden lg:block w-64 shrink-0 sticky top-16 h-[calc(100vh-4rem)] border-r border-border bg-card">
          {SidebarInner}
        </aside>

        <div className="lg:hidden sticky top-16 z-30 bg-background/90 backdrop-blur border-b border-border px-4 py-3 flex items-center justify-between">
          <div className="font-display text-lg">Admin</div>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button className="p-2 rounded-lg border border-border" data-testid="admin-mobile-menu"><Menu size={18}/></button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-72">
              {SidebarInner}
            </SheetContent>
          </Sheet>
        </div>

        <main className="flex-1 p-5 md:p-8 min-w-0">
          <Outlet/>
        </main>
      </div>
    </div>
  );
}
